import { Geometry } from 'geojson';

import { resolveV3 } from '@/endpoints/resolveV3';
import { LimitPaginatedResponse } from '@/models/endpoint';
import { isDefined } from '@/utils/guards';
import type { StateAbbrev, StateFips, StateName } from '@/utils/states';

export type County = {
  state_fips: StateFips;
  county_fips: string;
  full_fips: string;
  name: string;
  full_name: string;
  state_abbrev: StateAbbrev;
  state_name: StateName;
  county_slug: string;
  county_state_slug: string;
  geometry?: Geometry;
};

export type CountyQueryData = {
  location?: {
    lat: number;
    lon: number;
  };
  params?: {
    county_fips?: string;
    county_slug?: string;
    full_fips?: string;
    full_name__icontains?: string;
    /** Filter by 'County Name, State Abbrev' */
    full_county__like?: string;
    limit?: number;
    offset?: number;
    /**
     * - state_fips - State FIPS
     * - -state_fips - State FIPS (descending)
     * -  county_fips - County FIPS
     * -  -county_fips - County FIPS (descending)
     * -  full_fips - Full FIPS
     * -  -full_fips - Full FIPS (descending)
     * -  name - Name
     * -  -name - Name (descending)
     * -  full_name - Full Name
     * -  -full_name - Full Name (descending)
     * -  state_abbrev - 2-Char State Code
     * -  -state_abbrev - 2-Char State Code (descending)
     * -  state_name - State Name
     * -  -state_name - State Name (descending)
     * -  county_slug - County Slug
     * -  -county_slug - County Slug (descending)
     * -  county_state_slug - County+State Slug
     * -  -county_state_slug - County+State Slug (descending)
     */
    ordering?: string;
    /** 2 letter state abbreviation */
    state_abbrev?: string;
    /** 2 number state fips */
    state_fips?: string;
    include__geometry?: boolean;
  };
};

export async function getCounties(
  params: CountyQueryData,
  signal?: AbortSignal
): Promise<LimitPaginatedResponse<County>> {
  const queryParams = new URLSearchParams(
    params.params as Record<string, string>
  );

  // include geometry by default
  if (!isDefined(params.params?.include__geometry)) {
    queryParams.append('include__geometry', 'true');
  }

  if (params.location) {
    queryParams.append(
      'geometry__contains',
      `POINT (${params.location.lon} ${params.location.lat})`.toString()
    );
  }

  return resolveV3(
    {
      method: 'GET',
      params: queryParams,
      signal,
      url: `/counties/`,
    },
    {
      baseUrl: 'itasca',
    }
  );
}
